import { useCallback, useState } from 'react';

import { DownloadOutlined, InfoCircleOutlined, UploadOutlined } from '@ant-design/icons';
import type { Blob } from '@rails/activestorage';
import { Card, Tooltip, Typography } from 'antd';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router';

import type { IImportValidationError, IParticipantsImport } from '@/api/endpoints/schools/schoolParticipants';
import { useSendSchoolParticipantsImportsListsMutation } from '@/api/endpoints/schools/schoolParticipants';
import type { IApiError } from '@/api/types';
import { FileUploadField } from '@/shared/components/Fields/FileUploadField';
import { checkIsSpreadSheetFileValid } from '@/shared/helpers';

import { BulkImportModal } from '../Modals/BulkImportModal/BulkImportModal';

import { IMPORT_FILE_REQUIREMENTS, MIN_VALIDATION_MODAL_DISPLAY_MS, TRACK_ID_TIP_TEXT } from './consts';
import styles from './ParticipantsImport.module.scss';
import type { IFormData } from './types';

export const ParticipantsImport = () => {
  const [validationImportData, setValidationImportData] = useState<null | { filename: string }>(null);
  const [validationImportErrorData, setValidationImportErrorData] = useState<null | IImportValidationError[]>(null);
  const [importProgressData, setImportProgressData] = useState<null | IParticipantsImport>(null);

  const closeImportModal = useCallback(() => {
    setValidationImportData(null);
    setValidationImportErrorData(null);
    setImportProgressData(null);
  }, []);

  const [sendParticipantsImport] = useSendSchoolParticipantsImportsListsMutation();

  const { id: schoolId } = useParams();

  const methods = useForm<IFormData>({
    defaultValues: {
      file: null,
    },
  });

  const uploadImportFile = useCallback(
    async (blob: Blob) => {
      const startTime = Date.now();

      setValidationImportData({ filename: blob.filename });

      try {
        const response = await sendParticipantsImport({ schoolId: Number(schoolId), file: blob.signed_id }).unwrap();

        setImportProgressData(response);
      } catch (error) {
        const errData = error as IApiError;

        setValidationImportErrorData(errData.data.errors);
      } finally {
        const elapsedTime = Date.now() - startTime;
        const remainingTime = MIN_VALIDATION_MODAL_DISPLAY_MS - elapsedTime;

        if (remainingTime > 0) {
          await new Promise<void>((resolve) => {
            setTimeout(resolve, remainingTime);
          });
        }

        setValidationImportData(null);
      }
    },
    [schoolId],
  );

  return (
    <Card className={styles['import-wrapper']}>
      <FormProvider {...methods}>
        <FileUploadField
          acceptFormats=".csv,.xlsx"
          fieldName="file"
          requestCallback={uploadImportFile}
          fileCheckCb={checkIsSpreadSheetFileValid}
          className={styles['import-dragger']}
        >
          <UploadOutlined className={styles['import-image']} />
          <p className={styles['import-message']}>Перетащите сюда файл или нажмите, чтобы выбрать его</p>
          <div className={styles['import-requirements-wrapper']}>
            <p>Поддерживаемые форматы: CSV, XLSX</p>
            <p>Максимальный размер файла: 10 МБ</p>
          </div>
          <Typography.Link
            className={styles['download-example-link']}
            href="/files/schoolParticipantsImport/participants-import-example.xlsx"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <DownloadOutlined />
            Скачать шаблон файла
          </Typography.Link>
        </FileUploadField>
      </FormProvider>
      <Card className={styles['file-requirements-wrapper']}>
        <p className={styles['file-requirements-title']}>Требования к файлу:</p>
        <ul className={styles['file-requirements-list']}>
          {IMPORT_FILE_REQUIREMENTS.map((requirement, index) => (
            <li
              className={styles['file-requirements-item']}
              key={requirement}
            >
              <p>{requirement}</p>
              {index === 1 && (
                <Tooltip
                  styles={{ root: { maxWidth: 500 } }}
                  className={styles.tooltip}
                  title={<span className={styles['tooltip-text']}>{TRACK_ID_TIP_TEXT}</span>}
                >
                  <InfoCircleOutlined />
                </Tooltip>
              )}
            </li>
          ))}
        </ul>
      </Card>
      <BulkImportModal
        closeModal={closeImportModal}
        importProgressData={importProgressData}
        validationImportData={validationImportData}
        validationImportErrorData={validationImportErrorData}
      />
    </Card>
  );
};
